import { useEffect, useRef, useState } from "react";
import { TKeysOfType } from "../../../../shared/types";
import { Wizard } from "react-use-wizard";
import { IDataFeatures } from "../../../../shared/wizard/steps/featuresStep";
import AnimatedStep from "../../../../shared/wizard/steps/animatedStep";
import getTradeInSteps from "./steps/stepsArray/getStepsTradeIn";
import clsx from "clsx";
import ym from "react-yandex-metrika";
import { default as steps } from "./steps/tradeInSteps";
import { YANDEX_METRICS_ACCOUNT } from "../../../../../lib/constants";

import styles from "./styles.module.scss";

export interface IFillDataWizardTradeIn extends IDataFeatures {
  complexId: number | null;
  streetId: number | null;
  buildingId: number | null;
  areaInSqM: number | null;
  apartmentNumber: number | null;
  facilityTypeId: number | null;
  estimatedPrice: number | null;

  desiredLocationIds: number[] | null;
  rejectedLocationIds: number[] | null;

  selectedFacilityTypeIds: number[] | null;
  selectedFacilityTypeOwnFacility: number | null;
}

export type TStringKeysOfIFillDataWizardTradeIn = TKeysOfType<
  IFillDataWizardTradeIn,
  string | null
>;

export type TNumberKeysOfIFillDataWizardTradeIn = TKeysOfType<
  IFillDataWizardTradeIn,
  number | null | number[]
>;

export type TBooleanKeysOfIFillDataWizardTradeIn = TKeysOfType<
  IFillDataWizardTradeIn,
  boolean | null
>;

export type TKeyOfDataWizardTradeIn = keyof IFillDataWizardTradeIn;

const IS_PADDING_NARROW = window.innerWidth < 600;

const INITIAL_FILL_DATA_WIZARD = {
  complexId: null,
  streetId: null,
  buildingId: null,
  areaInSqM: null,
  apartmentNumber: null,
  facilityTypeId: null,
  estimatedPrice: null,

  encumbrance: null,
  childrenOwn: null,
  privatization: null,
  arrest: null,
  debt: null,

  desiredLocationIds: null,
  rejectedLocationIds: null,

  selectedFacilityTypeIds: null,
  selectedFacilityTypeOwnFacility: null,
};

// https://sboard.online/boards/8c2808b6-6787-4dd3-bf8e-f6f9666a8374 смотри схему в сборде. Ветвления разложены по папкам.

const TradeInWizard = () => {
  const [fillDataWizard, setFillDataWizard] = useState<IFillDataWizardTradeIn>(
    INITIAL_FILL_DATA_WIZARD
  );

  const [isWizardStarted, setIsWizardStarted] = useState(false);
  const [isWizardFinished, setIsWizardFinished] = useState(false);

  const handleChangeDataNumber = (
    data: number | number[],
    key: TNumberKeysOfIFillDataWizardTradeIn
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataObject = (data: Partial<IFillDataWizardTradeIn>) => {
    setFillDataWizard((prev) => {
      return { ...prev, ...data };
    });
  };

  const handleResetData = (
    keys: TKeyOfDataWizardTradeIn[],
    data?: number[]
  ) => {
    if (keys.length === 1) {
      const key = keys[0];
      setFillDataWizard((prev) => {
        return { ...prev, [key]: data ? data : null };
      });
    } else {
      const resetValues = keys.reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {} as Partial<IFillDataWizardTradeIn>);
      setFillDataWizard((prevData) => ({ ...prevData, ...resetValues }));
    }
  };

  const handleStepChange = (step: number) => {
    if (step === 1) {
      setIsWizardStarted(true);
    }
    if (
      step ===
      Math.max(...Array.from(Object.entries(steps), ([, value]) => value.step))
    ) {
      setIsWizardFinished(true);
    }
  };

  useEffect(() => {
    if (isWizardStarted && YANDEX_METRICS_ACCOUNT) {
      ym("reachGoal", "TradeInStarted");
    }
  }, [isWizardStarted]);

  useEffect(() => {
    if (isWizardFinished && YANDEX_METRICS_ACCOUNT) {
      ym("reachGoal", "TradeInFinished");
    }
  }, [isWizardFinished]);

  const previousStep = useRef<number>(0);

  const isNextStep_estimateWithoutApartment =
    fillDataWizard.areaInSqM &&
    fillDataWizard.buildingId &&
    fillDataWizard.streetId;

  const dataFormEstimate = {
    streetId: fillDataWizard.streetId,
    buildingId: fillDataWizard.buildingId,
    areaInSqM: fillDataWizard.areaInSqM,
    apartmentNumber: fillDataWizard.apartmentNumber,
  };

  const isDoublePrevSteps_location = !Boolean(
    fillDataWizard.rejectedLocationIds?.length
  );

  const isNextStepDisabled_selectedPolygonIds = !Boolean(
    fillDataWizard.desiredLocationIds?.length
  );
  const isNextStepDisabled_selectedTypeIds_first = !Boolean(
    fillDataWizard.selectedFacilityTypeIds?.length === 1
  );
  const isNextStepDisabled_selectedTypeIds_second = !Boolean(
    fillDataWizard.selectedFacilityTypeIds?.length === 2
  );

  const isNextStepDisabled_selectedFacilityTypeOwnFacility = !Boolean(
    fillDataWizard.selectedFacilityTypeOwnFacility
  );
  const isNextStepDisabled_apartmentNumber = !Boolean(
    fillDataWizard.apartmentNumber
  );
  const isNextStep_estimateWithoutApartment_b = !Boolean(
    isNextStep_estimateWithoutApartment
  );
  const isTriplePrevSteps_typeFacility = Boolean(
    fillDataWizard.selectedFacilityTypeIds?.length === 1
  );

  const tradeInSteps = getTradeInSteps(
    fillDataWizard,
    dataFormEstimate,
    handleChangeDataObject,
    handleChangeDataNumber,
    handleResetData,
    {
      isNextStepDisabled_apartmentNumber,
      isNextStep_estimateWithoutApartment:
        isNextStep_estimateWithoutApartment_b,
      isNextStepDisabled_selectedPolygonIds,
      isNextStepDisabled_selectedTypeIds_first,
      isNextStepDisabled_selectedTypeIds_second,
      isNextStepDisabled_selectedFacilityTypeOwnFacility,
      isDoublePrevSteps_location,
      isTriplePrevSteps_typeFacility,
    }
  );

  const allSteps = [...tradeInSteps];

  return (
    <Wizard
      wrapper={
        <div className={styles.tradeInWizard}>
          <div className={styles.tradeInWizard__container}></div>
        </div>
      }
      onStepChange={handleStepChange}
    >
      {allSteps.map((step, index) => {
        return (
          <AnimatedStep key={index} previousStep={previousStep}>
            <div
              className={clsx(
                styles.tradeInWizard__card,
                IS_PADDING_NARROW && styles.tradeInWizard__card_paddingNarrow
              )}
            >
              {/* <div className={styles.tradeInWizard__title}>
                Заявка на покупку квартиры в ТРЕЙД-ИН
              </div> */}
              <div className={styles.tradeInWizard__wizard}>{step}</div>
            </div>
          </AnimatedStep>
        );
      })}
    </Wizard>
  );
};

export default TradeInWizard;
